import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import Avatar from '@components/@core/Avatar';
import Button from '@components/@core/AnimatedButton';
import EditDropdown from '@components/EditDropdown';
import ProgressBar from '@components/@core/ProgressBar';
import { AppActions, VendorActions } from '~/actions';
import { EXPOSURE_LEVELS } from '~/services/constants';

const AllProducts = ({ data, onNavigation }) => {
    const dispatch = useDispatch();
    const categories = useSelector((state) => state.vendor.categories.all) || [];
    const appRole = useSelector((state) => state.app.primary_role_id);
    const isHCMSuperAdmin = appRole === 1;

    const ProductOptions = useMemo(() => {
        const options = ['Delete', 'Edit', 'View'];
        if (!isHCMSuperAdmin) {
            return filter((x) => x !== 'Delete', options);
        }
        return options;
    }, [isHCMSuperAdmin]);

    const handleOpenDetail = (product) => () => {
        onNavigation(`${product?.slug}?id=${data?.business_id}`);
    };

    const handleAddProduct = () => {
        const payload = {
            type: 'Basic Product Configuration',
            data: {
                before: { data: null, company: data, product: null },
                after: {
                    type: 'GETBUSINESS_REQUEST',
                    payload: { id: data?.business_id },
                },
            },
        };
        dispatch(AppActions.modalRequest(payload));
    };

    const handleEditProduct = (product) => {
        const payload = {
            type: 'Basic Product Configuration',
            data: {
                before: { data: null, company: data, product },
                after: {
                    type: 'GETBUSINESS_REQUEST',
                    payload: { id: data?.business_id },
                },
            },
        };
        if (product.data?.sponsored_level === 'gold') payload.type = 'Premium Product Configuration';
        if (product.data?.sponsored_level === 'silver') payload.type = 'Plus Product Configuration';
        dispatch(AppActions.modalRequest(payload));
    };

    const handleDelete = (product) => {
        dispatch(
            AppActions.modalRequest({
                type: 'Confirm',
                data: {
                    before: {
                        title: 'Delete',
                        body: 'This will delete the product. This cannot be undone. Are you sure?',
                    },
                },
                callBack: {
                    onYes: () => {
                        dispatch(
                            VendorActions.postsavecontentRequest(
                                {
                                    content: {
                                        ...product,
                                        deleted_at: moment(new Date()).format('YYYY-MM-DD h:mm:ss'),
                                    },
                                },
                                { businessId: product.business_id }
                            )
                        );
                    },
                },
            })
        );
    };

    const handleSelectOption = (product) => (e) => {
        switch (e) {
            case 'view':
                handleOpenDetail(product)();
                break;

            case 'edit':
                handleEditProduct(product);
                break;

            case 'delete':
                handleDelete(product);
                break;

            default:
                break;
        }
    };

    return (
        <div className="card pt-3">
            <Row className="mx-0 border-bottom desktop-flex">
                <Col xs={6} md={3} className="px-0">
                    <p className="dsl-b12">Name</p>
                </Col>
                <Col xs={6} md={3} className="px-0">
                    <p className="dsl-b12">Category</p>
                </Col>
                <Col xs={4} md={2} className="px-0">
                    <p className="dsl-b12">Exposure Level</p>
                </Col>
                <Col xs={6} md={3} className="px-0">
                    <p className="dsl-b12">Optimization</p>
                </Col>
                <Col xs={2} md={1} className="px-0"></Col>
            </Row>
            {data?.products &&
                data.products.map((product) => {
                    const category = find(propEq('id', product.data?.category), categories);
                    const level = find(propEq('value', product.data?.sponsored_level || 'wiki'), EXPOSURE_LEVELS);
                    return (
                        <Row className="py-3 mx-0 border-bottom" key={product.id}>
                            <Col xs={12} md={3} sm={6} className="d-h-start justify-content-between px-0" onClick={handleOpenDetail(product)}>
                                <div className="d-flex align-items-center">
                                    <Avatar name={product.name} />
                                    <p className="dsl-b14 text-400 ms-2 mb-0 justify-content-center truncate-one word-break">{product.name}</p>
                                </div>
                                <div className="mobile-screen">
                                    <EditDropdown options={ProductOptions} onChange={handleSelectOption(product)} />
                                </div>
                            </Col>
                            <Col xs={12} sm={6} md={3} className="d-h-start px-0 my-1 my-sm-0" onClick={handleOpenDetail(product)}>
                                <label className="dsl-b14 w-30 mobile-block">Category</label>
                                <p className="dsl-b14 text-400 mb-0">{product.category?.name ?? category?.name}</p>
                            </Col>
                            <Col xs={12} sm={4} md={2} className="d-h-start px-0 my-1 my-sm-0" onClick={handleOpenDetail(product)}>
                                <label className="dsl-b14 w-30 mobile-block">Level</label>
                                <p className="dsl-b14 text-400 mb-0">{level?.label || 'Basic'}</p>
                            </Col>
                            <Col xs={12} sm={6} md={3} className="d-h-start px-0 my-1 my-sm-0" onClick={handleOpenDetail(product)}>
                                <label className="dsl-b14 w-30 mobile-block">Optimization</label>
                                <ProgressBar horizontal value={100} />
                            </Col>
                            <Col xs={12} sm={2} md={1} className="d-h-end px-0 desktop-flex">
                                <EditDropdown options={ProductOptions} onChange={handleSelectOption(product)} />
                            </Col>
                        </Row>
                    );
                })}
            <div className="d-h-end pt-4">
                <Button name="Add Product" onClick={handleAddProduct} />
            </div>
        </div>
    );
};

AllProducts.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        avatar: PropTypes.string,
        business_id: PropTypes.number,
        cover_url: PropTypes.string,
        products: PropTypes.array,
        title_url: PropTypes.string,
    }),
};

AllProducts.defaultProps = {
    data: {
        id: 1,
        name: '',
        avatar: '',
        business_id: 1,
        cover_url: '',
        products: [],
        title_url: '',
    },
};

export default AllProducts;
